import { useState } from "react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { Mail, AlertTriangle, Send } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { showNotification } from "../../../store/Central/selectors";

type EmailTemplate = "shared-file" | "dentlab-vendor-invite" | "dentconnect-invite";

interface FormData {
	emailAddress: string;
	fileName: string;
	link: string;
	message: string;
	labName: string;
}

const initialFormData: FormData = {
	emailAddress: "julius.janda+test-email@dentdesk.ch",
	fileName: "Rechnung_2024.pdf",
	link: "https://dentconnect.dentdesk.ch/?fileId=test123",
	message: "Ihr Dokument steht zum sicheren Download bereit.",
	labName: "Muster Labor AG",
};

const templateConfig: Record<EmailTemplate, { label: string; fields: (keyof FormData)[]; description: string }> = {
	"shared-file": {
		label: "Datei geteilt",
		description: "Benachrichtigung, dass eine Datei geteilt wurde",
		fields: ["emailAddress", "fileName", "link", "message"],
	},
	"dentlab-vendor-invite": {
		label: "dentLAB Vendor Einladung",
		description: "Einladung für einen Lieferanten zur Zusammenarbeit",
		fields: ["emailAddress", "labName"],
	},
	"dentconnect-invite": {
		label: "dentCONNECT Einladung",
		description: "Einladung für einen Zahnarzt zu dentCONNECT",
		fields: ["emailAddress", "labName", "message"],
	},
};

const fieldLabels: Record<keyof FormData, { label: string; placeholder: string }> = {
	emailAddress: { label: "E-Mail Adresse", placeholder: "empfaenger@example.com" },
	fileName: { label: "Dateiname", placeholder: "Rechnung_2024.pdf" },
	link: { label: "Link", placeholder: "https://dentconnect.dentdesk.ch/?..." },
	message: { label: "Nachricht", placeholder: "Ihre Nachricht..." },
	labName: { label: "Labor Name", placeholder: "Muster Labor AG" },
};

export const EmailSenderPage = () => {
	const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>("shared-file");
	const [formData, setFormData] = useState<FormData>(initialFormData);
	const [isLoading, setIsLoading] = useState(false);

	const handleFieldChange = (field: keyof FormData, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleTemplateChange = (template: EmailTemplate) => {
		setSelectedTemplate(template);
		setFormData(initialFormData);
	};

	const handleSend = async () => {
		setIsLoading(true);
		try {
			if (selectedTemplate === "shared-file") {
				await trpc.email.sendSharedFile.mutate({
					recipientEmail: formData.emailAddress,
					fileName: formData.fileName,
					link: formData.link,
					message: formData.message,
				});
			} else if (selectedTemplate === "dentlab-vendor-invite") {
				await trpc.email.sendDentlabVendorInvite.mutate({
					recipientEmail: formData.emailAddress,
					labName: formData.labName,
				});
			} else if (selectedTemplate === "dentconnect-invite") {
				await trpc.email.sendDentconnectInvite.mutate({
					recipientEmail: formData.emailAddress,
					labName: formData.labName,
					message: formData.message,
				});
			}
			showNotification({ message: "E-Mail erfolgreich gesendet", type: "success" });
			setFormData(initialFormData);
		} catch (error) {
			console.error("Failed to send email:", error);
			showNotification({ message: "Fehler beim Senden der E-Mail", type: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	const currentConfig = templateConfig[selectedTemplate];
	const isFormValid = currentConfig.fields.every((field) => formData[field].trim() !== "");

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Support" }, { name: "E-Mail Sender" }]}
					icon={<Mail width="16px" height="16px" color="rgb(55, 65, 81)" />}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-bold text-gray-900">E-Mail Sender</h1>
					<p className="text-gray-600">
						Versenden Sie E-Mail-Vorlagen direkt aus dem Support-Bereich.
					</p>
				</div>

				<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-amber-900">Achtung</h3>
							<p className="text-amber-800 text-sm mt-1">
								Diese E-Mails werden tatsächlich versendet. Stellen Sie sicher,
								dass Sie die korrekte E-Mail-Adresse eingeben.
							</p>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">Vorlage auswählen</h3>
							<div className="flex flex-col gap-2">
								{(Object.keys(templateConfig) as EmailTemplate[]).map((template) => (
									<button
										key={template}
										onClick={() => handleTemplateChange(template)}
										className={`text-left px-4 py-3 rounded-lg border transition-colors ${
											selectedTemplate === template
												? "bg-blue-50 border-blue-300 text-blue-900"
												: "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
										}`}
										data-testid={`template-${template}`}
										aria-label={templateConfig[template].label}
									>
										<div className="font-medium">{templateConfig[template].label}</div>
										<div className="text-sm text-gray-500 mt-1">
											{templateConfig[template].description}
										</div>
									</button>
								))}
							</div>
						</div>

						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">Formular</h3>
							<div className="space-y-4">
								{currentConfig.fields.map((field) => (
									<div key={field}>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											{fieldLabels[field].label}
										</label>
										{field === "message" ? (
											<textarea
												value={formData[field]}
												onChange={(e) => handleFieldChange(field, e.target.value)}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
												placeholder={fieldLabels[field].placeholder}
												rows={3}
												data-testid={`input-${field}`}
												aria-label={fieldLabels[field].label}
											/>
										) : (
											<input
												type={field === "emailAddress" ? "email" : "text"}
												value={formData[field]}
												onChange={(e) => handleFieldChange(field, e.target.value)}
												className="w-full px-3 py-2 border border-gray-300 rounded-md"
												placeholder={fieldLabels[field].placeholder}
												data-testid={`input-${field}`}
												aria-label={fieldLabels[field].label}
											/>
										)}
									</div>
								))}
							</div>

							<button
								onClick={handleSend}
								disabled={!isFormValid || isLoading}
								className={`mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
									isFormValid && !isLoading
										? "bg-blue-600 text-white hover:bg-blue-700"
										: "bg-gray-100 text-gray-400 cursor-not-allowed"
								}`}
								data-testid="send-button"
								aria-label="E-Mail senden"
							>
								<Send className="w-4 h-4" />
								{isLoading ? "Wird gesendet..." : "E-Mail senden"}
							</button>
						</div>
					</div>

					<div className="flex flex-col gap-4">
						<div className="bg-white border border-gray-200 rounded-lg p-6">
							<h3 className="text-lg font-semibold mb-4">Vorschau</h3>
							<div className="bg-gray-50 rounded-lg p-4">
								<div className="space-y-3 text-sm">
									<div>
										<span className="font-medium text-gray-600">Vorlage:</span>{" "}
										<span className="text-gray-900">{currentConfig.label}</span>
									</div>
									{currentConfig.fields.map((field) => (
										<div key={field}>
											<span className="font-medium text-gray-600">
												{fieldLabels[field].label}:
											</span>{" "}
											<span className="text-gray-900">
												{formData[field] || <span className="text-gray-400 italic">nicht ausgefüllt</span>}
											</span>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
